import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { AlertTriangle, FileText, Calendar, Hash, CheckCircle2 } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { DataTable, type Column } from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import {
  EntityCardGrid,
  MetaDates,
  StatTile,
  StatTiles,
  TitleTooltip,
} from "../components/EntityCardGrid";
import {
  safeFormatDate,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import { searchPoams, ApiPoam, ApiPoamItem } from "../../api/frameworksApi";

const REMEDIATED_STATUSES = ["completed", "closed_remediated", "remediated"];

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST /poams/{page}/{size}. Sorting is fully server-side; a key with no
 * entry here is forwarded as-is.
 */
const POAM_SORT_COLUMNS: Record<string, string> = {
  sspName: "sspName",
  title: "title",
  itemCount: "itemCount",
  createdAt: "createdAt",
  updatedAt: "updatedAt",
};

function remediatedCount(p: ApiPoam): number {
  if (typeof p.remediatedCount === "number") return p.remediatedCount;
  return (p.items ?? []).filter(
    (i: ApiPoamItem) =>
      REMEDIATED_STATUSES.includes((i.status ?? "").toLowerCase()),
  ).length;
}

export function POAM() {
  const navigate = useNavigate();

  /* ── List state ──
   * Search, sorting and pagination are all resolved by the backend via
   * POST /poams/{page}/{size}; `poams` always holds exactly the rows for the
   * current page and is rendered as-is. */
  const [poams, setPoams] = useState<ApiPoam[]>([]);
  const [loading, setLoading] = useState(true);
  // True only while a *subsequent* page/sort/search fetch is in flight, so the
  // previous rows stay on screen instead of flashing back to a skeleton.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // plain table default (10) isn't one of [12,24,48,96].
  const [pageSize, setPageSize] = useState(getPageSizeOptions("card")[0]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const fetchData = useCallback(async () => {
    setError(null);
    if (firstLoadDoneRef.current) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await searchPoams(page, pageSize, {
        keyword: keyword.trim(),
        // Unsorted (the initial state) sends both sort fields as empty.
        sortByColumn: sortKey ? (POAM_SORT_COLUMNS[sortKey] ?? sortKey) : "",
        sortingOrder: sortKey ? sortDir : "",
        // This list has always hidden POA&Ms with no findings.
        hasItems: true,
      });
      setPoams(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower search) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load POA&M data");
      setPoams([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, pageSize, keyword, sortKey, sortDir]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPage(0);
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order and page window. */
  const paged = poams;

  const SortIcon = ({ col }: { col: string }) => {
    const active = sortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && sortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && sortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const columns: Column<ApiPoam>[] = [
    {
      key: "no",
      header: "#",
      align: "center",
      render: (_row, idx) => (
        <span className="text-[#0f172a] dark:text-white/80 font-medium">
          {page * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "sspName",
      align: "left",
      ellipsis: 2,
      tooltip: (p) =>
        p.sspName || p.importSspHref?.replace(/^urn:uuid:/, "") || "",
      header: (
        <button
          onClick={() => handleSort("sspName")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          SSP Name
          <SortIcon col="sspName" />
        </button>
      ),
      render: (p) => (
        <p className="font-medium text-[#0f172a] dark:text-white/80">
          {p.sspName ||
            p.importSspHref?.replace(/^urn:uuid:/, "").slice(0, 12) ||
            "-"}
        </p>
      ),
    },
    {
      key: "title",
      align: "left",
      ellipsis: 2,
      tooltip: (p) => p.title || p.documentId || "",
      header: (
        <button
          onClick={() => handleSort("title")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          POA&amp;M Title
          <SortIcon col="title" />
        </button>
      ),
      render: (p) => (
        <div className="flex items-center gap-2">
          <FileText className="size-3.5 text-[#94a3b8] dark:text-white/55 flex-shrink-0" />
          <p className="text-sm font-medium text-[#0f172a] dark:text-white/80">
            {p.title || p.documentId}
          </p>
        </div>
      ),
    },
    {
      key: "itemCount",
      align: "center",
      header: (
        <button
          onClick={() => handleSort("itemCount")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Findings
          <SortIcon col="itemCount" />
        </button>
      ),
      render: (p) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-bold bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400">
          <Hash className="size-3" />
          {p.itemCount ?? p.items?.length ?? 0}
        </span>
      ),
    },
    {
      key: "createdAt",
      align: "center",
      header: (
        <button
          onClick={() => handleSort("createdAt")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Created On
          <SortIcon col="createdAt" />
        </button>
      ),
      render: (p) => (
        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0f172a] dark:text-white/80 whitespace-nowrap">
          <Calendar className="size-3 text-[#94a3b8] dark:text-white/55" />
          {new Date(p.createdAt).toLocaleDateString("en-GB")}
        </div>
      ),
    },
    {
      key: "updatedAt",
      align: "center",
      header: (
        <button
          onClick={() => handleSort("updatedAt")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Updated On
          <SortIcon col="updatedAt" />
        </button>
      ),
      render: (p) => (
        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0f172a] dark:text-white/80 whitespace-nowrap">
          <Calendar className="size-3 text-[#94a3b8] dark:text-white/55" />
          {new Date(p.updatedAt || p.createdAt).toLocaleDateString("en-GB")}
        </div>
      ),
    },
  ];

  return (
    <PageShell className="flex flex-col flex-1 min-h-0 gap-6">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between">
        <div>
          <h1>Plan of Action and Milestones (POA&amp;M)</h1>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
            View and manage Plan of Action and Milestones (POA&amp;M) records for linked system security plans.
          </p>
        </div>
      </div>

      <EntityCardGrid<ApiPoam>
        items={paged}
        loading={loading}
        getKey={(p) => p.id}
        getStatus={() => "draft"}
        hideStatusBadge
        onOpen={(p) => navigate(`/continuous-compliance/risk/poam/${p.id}`)}
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(0);
        }}
        pageSizeOptions={[...getPageSizeOptions(viewMode)]}
        itemLabel="records"
        emptyText={
          search
            ? "No POA&M records match your search"
            : "No POA&M records available"
        }
        emptyIcon={
          <AlertTriangle className="size-5 text-[#cbd5e1] dark:text-white/15" />
        }
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          setPageSize(resolvePageSizeForViewMode(pageSize, mode));
          setPage(0);
        }}
        // The whole toolbar row is the TableToolbar below — search, view
        // toggle and rows-per-page all come from it.
        hideBuiltInControls
        toolbarLeft={
          !loading && !error ? (
            <TableToolbar
              search={{
                value: search,
                onChange: setSearch,
                onSubmit: submitSearch,
                placeholder: "Search by SSP or Title",
                ariaLabel: "Search POA&M records",
                disabled: refreshing && !poams.length,
              }}
              viewMode={viewMode}
              onViewModeChange={(mode) => {
                setViewMode(mode);
                setPageSize(resolvePageSizeForViewMode(pageSize, mode));
                setPage(0);
              }}
              pageSize={pageSize}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
              }}
              pageSizeOptions={[...getPageSizeOptions(viewMode)]}
              pageSizeLabel={
                viewMode === "table" ? "Rows per page" : "Cards per page"
              }
            />
          ) : undefined
        }
        renderTitle={(p) => (
          <TitleTooltip text={p.title || p.documentId || "Untitled POA&M"}>
            <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {p.title || p.documentId || "Untitled POA&M"}
            </h3>
          </TitleTooltip>
        )}
        renderHeaderRight={() => (
          <FileText className="size-4 text-indigo-500 dark:text-indigo-400" />
        )}
        renderBadges={(p) => {
          const sspName =
            p.sspName ||
            p.importSspHref?.replace(/^urn:uuid:/, "").slice(0, 12) ||
            null;
          return (
            sspName && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap max-w-full truncate px-1.5 py-0.5 rounded-md text-[10px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20">
                <FileText className="size-3 flex-shrink-0" />
                <span className="truncate">{sspName}</span>
              </span>
            )
          );
        }}
        renderStats={(p) => (
          <StatTiles>
            <StatTile
              icon={<Hash className="size-3.5" />}
              accent="indigo"
              value={p.itemCount ?? p.items?.length ?? 0}
              label="Findings"
            />
            <StatTile
              icon={<CheckCircle2 className="size-3.5" />}
              accent="emerald"
              value={remediatedCount(p)}
              label="Remediated"
            />
          </StatTiles>
        )}
        renderDates={(p) => (
          <MetaDates
            created={safeFormatDate(p.createdAt)}
            updated={safeFormatDate(p.updatedAt || p.createdAt)}
          />
        )}
        renderTable={(rows) => (
          <DataTable<ApiPoam>
            columns={columns}
            data={rows}
            loading={loading}
            error={error}
            onRetry={fetchData}
            emptyText={
              search
                ? "No POA&M records match your search"
                : "No POA&M records available"
            }
            emptyIcon={
              <AlertTriangle className="size-5 text-[#cbd5e1] dark:text-white/15" />
            }
            onRowClick={(p) => navigate(`/continuous-compliance/risk/poam/${p.id}`)}
            rowKey={(p) => p.id}
            itemLabel="records"
          />
        )}
      />
    </PageShell>
  );
}
